// Parameter Serializer - Secure serialization of query parameters

const ALLOWED_TYPES = ['string', 'number', 'boolean'];
const MAX_PARAM_LENGTH = 1000;
const MAX_ARRAY_LENGTH = 100;

// Dangerous patterns to sanitize from string values
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  /data:/gi,
  /vbscript:/gi,
];

// Sanitize a string value
const sanitizeString = (value) => {
  if (typeof value !== 'string') {
    return String(value);
  }

  let sanitized = value;

  if (sanitized.length > MAX_PARAM_LENGTH) {
    console.warn('[ParamSerializer] Parameter value truncated - exceeded max length');
    sanitized = sanitized.substring(0, MAX_PARAM_LENGTH);
  }

  DANGEROUS_PATTERNS.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, '');
  });

  return sanitized.trim();
};

// Validate and sanitize a parameter value
const validateValue = (value, key) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_LENGTH) {
      console.warn(`[ParamSerializer] Array parameter "${key}" truncated - exceeded max length`);
      value = value.slice(0, MAX_ARRAY_LENGTH);
    }
    return value.map((item, index) => validateValue(item, `${key}[${index}]`)).filter((v) => v !== null);
  }

  if (typeof value === 'object') {
    console.warn(`[ParamSerializer] Object parameter "${key}" - will be JSON stringified`);
    try {
      return JSON.stringify(value);
    } catch (e) {
      console.error(`[ParamSerializer] Failed to stringify object parameter "${key}"`);
      return null;
    }
  }

  if (!ALLOWED_TYPES.includes(typeof value)) {
    console.warn(`[ParamSerializer] Invalid parameter type for "${key}": ${typeof value}`);
    return null;
  }

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  return value;
};

// Serialize an object to URL query parameters
export const serializeParams = (params) => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return '';
  }

  // Prevent prototype pollution
  if ('__proto__' in params || 'constructor' in params || 'prototype' in params) {
    console.error('[ParamSerializer] Potential prototype pollution detected');
    return '';
  }

  const parts = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    const sanitizedValue = validateValue(value, key);
    if (sanitizedValue === null) {
      return;
    }

    const sanitizedKey = encodeURIComponent(sanitizeString(key));

    if (Array.isArray(sanitizedValue)) {
      sanitizedValue.forEach((item) => {
        parts.push(`${sanitizedKey}=${encodeURIComponent(String(item))}`);
      });
    } else {
      parts.push(`${sanitizedKey}=${encodeURIComponent(String(sanitizedValue))}`);
    }
  });

  return parts.join('&');
};

// Build a complete URL with query parameters
export const buildUrl = (baseUrl, params = {}) => {
  if (!baseUrl || typeof baseUrl !== 'string') {
    console.error('[ParamSerializer] Invalid base URL');
    return '';
  }

  const queryString = serializeParams(params);
  
  if (!queryString) {
    return baseUrl;
  }

  // Check if URL already has query parameters
  const separator = baseUrl.includes('?') ? '&' : '?';
  
  return `${baseUrl}${separator}${queryString}`;
};

// Parse query string back to object
export const parseParams = (queryString) => {
  if (!queryString || typeof queryString !== 'string') {
    return {};
  }

  const cleanQuery = queryString.startsWith('?') ? queryString.slice(1) : queryString;
  const params = {};

  cleanQuery.split('&').forEach((part) => {
    const [key, value] = part.split('=').map(decodeURIComponent);
    
    if (!key) return;

    if (key in params) {
      if (Array.isArray(params[key])) {
        params[key].push(value);
      } else {
        params[key] = [params[key], value];
      }
    } else {
      params[key] = value;
    }
  });

  return params;
};

export default {
  serializeParams,
  buildUrl,
  parseParams,
};
